import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/auth';
import { useWorkspace } from '../contexts/WorkspaceContext';

// ─── Section component ────────────────────────────────────────────────────────

function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <div className="divide-y divide-gray-100">{children}</div>
    </section>
  );
}

function SettingsRow({
  label,
  value,
  action,
  actionLabel,
  href,
  soon,
}: {
  label: string;
  value?: string;
  action?: () => void;
  actionLabel?: string;
  href?: string;
  soon?: boolean;
}) {
  return (
    <div className="flex items-center justify-between px-5 py-3.5 gap-4">
      <div className="min-w-0">
        <p className="text-sm text-gray-800">{label}</p>
        {value && <p className="text-xs text-gray-500 mt-0.5 truncate">{value}</p>}
      </div>
      {soon && (
        <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 uppercase tracking-wide">
          Soon
        </span>
      )}
      {!soon && href && (
        <Link to={href} className="shrink-0 text-xs text-primary-600 hover:text-primary-800 font-medium">
          {actionLabel ?? 'Edit'}
        </Link>
      )}
      {!soon && action && (
        <button onClick={action} className="shrink-0 text-xs text-primary-600 hover:text-primary-800 font-medium">
          {actionLabel ?? 'Edit'}
        </button>
      )}
    </div>
  );
}

// ─── SettingsPage ─────────────────────────────────────────────────────────────

export function SettingsPage() {
  const { user } = useAuth();
  const { effectivePlan, canManageBilling, planOwnerName } = useWorkspace();

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
      <h1 className="text-xl font-semibold text-gray-900 mb-6">Settings</h1>

      {/* Account */}
      <SettingsSection title="Account" description="Manage your personal information.">
        <SettingsRow label="Name" value={user?.name ?? '—'} href="/profile" actionLabel="Edit" />
        <SettingsRow label="Email" value={user?.email ?? '—'} href="/profile" actionLabel="Edit" />
        <SettingsRow label="Profile photo & bio" href="/profile" actionLabel="Edit" />
      </SettingsSection>

      {/* Privacy & Data */}
      <SettingsSection
        title="Privacy & Data"
        description="Control how your data is stored and used."
      >
        <SettingsRow label="Export my data" actionLabel="Request export" soon />
        <SettingsRow label="Delete account" actionLabel="Delete" soon />
        <SettingsRow
          label="Cookie preferences"
          actionLabel="Manage"
          action={() => {
            // Re-open cookie consent banner
            localStorage.removeItem('cookie-consent');
            window.location.reload();
          }}
        />
        <SettingsRow label="Privacy policy" href="/privacy" actionLabel="View" />
        <SettingsRow label="Terms of service" href="/terms" actionLabel="View" />
      </SettingsSection>

      {/* Notifications */}
      <SettingsSection
        title="Notifications"
        description="Choose what you're notified about."
      >
        <SettingsRow label="Email notifications" soon />
        <SettingsRow label="Push notifications" soon />
        <SettingsRow label="Approval requests" soon />
      </SettingsSection>

      {/* Subscription */}
      {canManageBilling ? (
        <SettingsSection title="Subscription" description="Your plan and billing details.">
          <SettingsRow
            label="Current plan"
            value={effectivePlan === 'family' ? 'Family' : 'Free'}
          />
          <SettingsRow label="Billing" actionLabel="Manage" href="/pricing" />
          <SettingsRow label="View pricing" href="/pricing" actionLabel="View" />
        </SettingsSection>
      ) : (
        <SettingsSection title="Subscription">
          <SettingsRow
            label="Plan"
            value={planOwnerName ? `Managed by ${planOwnerName}` : (effectivePlan === 'family' ? 'Family' : 'Free')}
          />
        </SettingsSection>
      )}

      {/* About */}
      <SettingsSection title="About">
        <SettingsRow label="Version" value="Beta" />
        <SettingsRow label="Privacy policy" href="/privacy" actionLabel="View" />
        <SettingsRow label="Cookie policy" href="/cookies" actionLabel="View" />
      </SettingsSection>
    </div>
  );
}
